'use client'

/* eslint-disable no-console */
import { useEffect } from 'react'
import { sendEnterpriseEvent } from '@utils/enterpriseSegmentEvent'
import { TAP_EVENT, EVENT_TYPE } from '@constants/enterpriseSegmentData'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { getConfig } from '@configs/envConfig'
import { TENANT_TYPE } from '@components/Ackodrive/common/constants'
import logger from '@utils/logger'

const TARGET_FORM_ID = 'd5018fa1-ed08-4739-9390-20d27da5d840'
const CALENDLY_ROUTING_FORM_URL =
  'https://calendly.com/api/form_builder/forms/2b47ae52-7d2c-415f-beb4-2d097acfbba0/submissions'

declare global {
  interface Window {
    HubSpotFormsV4: {
      getForms: () => Array<{
        getFormFieldValues: () => Promise<Record<string, any>>
      }>
      getFormFromEvent: (event: any) => {
        getFormFieldValues: () => Promise<Record<string, any>>
      }
    }
    Calendly?: {
      initHubspotForm: (config: { id: string; url: string }) => void
    }
  }
}

// Map HubSpot field names to required key names
const FIELD_NAME_MAP: Record<string, string> = {
  firstname: 'first_name',
  company: 'company_name',
  eb_client_industry: 'industry',
  numemployees: 'num_of_employees',
  phone: 'phone',
  email: 'email',
  message: 'message',
  interested_in: 'interested_in',
}

// Transform HubSpot form values to flat key-value pairs with renamed keys
const transformFormValues = (values: Record<string, any>): Record<string, any> => {
  const transformedData: Record<string, any> = {}

  Object.keys(values).forEach(key => {
    const field = values[key]
    if (field && typeof field === 'object' && 'name' in field && 'value' in field) {
      // Extract field name (remove "0-1/" prefix)
      const fieldName = field.name.split('/').pop() || field.name
      // Map to the required key name
      const mappedFieldName = FIELD_NAME_MAP[fieldName] || fieldName
      transformedData[mappedFieldName] = field.value
    }
  })

  return transformedData
}

const EnterpriseFormTracker = () => {
  const config = useEnvVariablesClientConfig()
  const enterpriseWriteKey = getConfig(config?.ACKO_ENV)?.segmentKey?.[TENANT_TYPE.ACKOENTERPRISE]

  useEffect(() => {
    let calendlyInitialized = false
    let isUnmounted = false
    let observer: MutationObserver | null = null
    let popupObserver: MutationObserver | null = null

    const targetFormSelector = [
      `form[data-form-id="${TARGET_FORM_ID}"]`,
      `.hs-form[data-form-id="${TARGET_FORM_ID}"]`,
      `.hs-form-frame[data-form-id="${TARGET_FORM_ID}"]`,
      `[data-hubspot-form-id="${TARGET_FORM_ID}"]`,
      `#hsForm_${TARGET_FORM_ID}`,
    ].join(',')

    const hasTargetForm = () => Boolean(document.querySelector(targetFormSelector))

    const initCalendlyForm = () => {
      if (isUnmounted || calendlyInitialized || !window.Calendly?.initHubspotForm) {
        return
      }
      if (!hasTargetForm()) {
        return
      }
      window.Calendly.initHubspotForm({
        id: TARGET_FORM_ID,
        url: CALENDLY_ROUTING_FORM_URL,
      })
      calendlyInitialized = true
    }

    const ensureTargetFormPresence = () => {
      if (calendlyInitialized) {
        return
      }
      if (hasTargetForm()) {
        initCalendlyForm()
        if (observer) {
          observer.disconnect()
          observer = null
        }
        return
      }
      if (observer || typeof MutationObserver === 'undefined') {
        return
      }
      observer = new MutationObserver(() => {
        if (hasTargetForm()) {
          initCalendlyForm()
          if (observer) {
            observer.disconnect()
            observer = null
          }
        }
      })
      observer.observe(document.body, { childList: true, subtree: true })
    }

    const addCalendlyCss = () => {
      if (document.querySelector('link[data-calendly-widget-css]')) {
        return
      }
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://assets.calendly.com/assets/external/widget.css'
      link.dataset.calendlyWidgetCss = 'true'
      document.head.appendChild(link)
    }

    const ensureCalendlyScript = () =>
      new Promise<void>((resolve, reject) => {
        if (window.Calendly?.initHubspotForm) {
          resolve()
          return
        }

        const existingScript = document.querySelector<HTMLScriptElement>('script[data-calendly-widget-forms]')
        if (existingScript) {
          existingScript.addEventListener('load', () => resolve(), { once: true })
          existingScript.addEventListener('error', () => reject(), { once: true })
          return
        }

        const script = document.createElement('script')
        script.src = 'https://assets.calendly.com/assets/external/forms.js'
        script.async = true
        script.dataset.calendlyWidgetForms = 'true'
        script.addEventListener('load', () => resolve(), { once: true })
        script.addEventListener('error', () => reject(), { once: true })
        document.body.appendChild(script)
      })

    const ensureCalendlyPopupObserver = () => {
      if (popupObserver || typeof MutationObserver === 'undefined') {
        return
      }
      popupObserver = new MutationObserver(() => {
        const popupContent = document.querySelector<HTMLElement>('.calendly-popup-content')
        if (!popupContent) {
          return
        }
        const dataUrl = popupContent.getAttribute('data-url')
        if (!!dataUrl && !dataUrl.includes('undefined')) {
          return
        }
        const overlay = document.querySelector<HTMLElement>('.calendly-overlay')
        if (overlay) {
          overlay.remove()
        }
        logger.error('Calendly routing form returned an invalid URL', { dataUrl })
      })
      popupObserver.observe(document.body, { childList: true, subtree: true })
    }

    addCalendlyCss()
    void ensureCalendlyScript()
      .then(() => {
        // Run on load to match Calendly recommendation
        if (document.readyState === 'complete') {
          ensureTargetFormPresence()
        }
        ensureCalendlyPopupObserver()
      })
      .catch(() => {})

    const onWindowLoad = () => {
      ensureTargetFormPresence()
    }

    window.addEventListener('load', onWindowLoad)

    const handleFormSubmission = async (event: any) => {
      const formId = event?.detail?.formId

      if (formId !== TARGET_FORM_ID) {
        return
      }

      // Check if HubSpot library is loaded
      if (!window.HubSpotFormsV4?.getFormFromEvent) {
        return
      }

      try {
        // Use HubSpot API to get form field values
        const form = window.HubSpotFormsV4.getFormFromEvent(event)
        const values = await form.getFormFieldValues()

        // Transform form values
        const transformedData = transformFormValues(values)

        // Get additional page properties
        const pathname = window.location.pathname
        const search = window.location.search
        const title = document.title
        const referrer = document.referrer || ''

        const payload = {
          ...transformedData,
          page_type: '',
          path: pathname,
          source: referrer,
          referrer,
          title,
          search,
          form_id: formId,
          action_name: 'lead_form_submitted',
          action_type: TAP_EVENT.SUBMIT_LEAD_FORM,
        }

        void sendEnterpriseEvent(payload, EVENT_TYPE.ACTION, enterpriseWriteKey)
      } catch (error) {}
    }

    const wrappedHandler = (event: any) => {
      void handleFormSubmission(event)
    }

    window.addEventListener('hs-form-event:on-submission:success', wrappedHandler)

    return () => {
      isUnmounted = true
      if (observer) {
        observer.disconnect()
        observer = null
      }
      if (popupObserver) {
        popupObserver.disconnect()
        popupObserver = null
      }
      window.removeEventListener('load', onWindowLoad)
      window.removeEventListener('hs-form-event:on-submission:success', wrappedHandler)
    }
  }, [enterpriseWriteKey])

  return null
}

export default EnterpriseFormTracker
